/** Maximum total storage allowed per workspace, in bytes. */
export const WORKSPACE_MAX_SIZE_BYTES = 10 * 1024 * 1024;

/**
 * Tone of voice a workspace applies to enrichment output. Added by the backend
 * lead-pipelines migration; legacy workspaces default to `consultative`.
 */
export type WorkspaceTone = 'consultative' | 'direct' | 'friendly';

/** Default tone used when the API omits the field (legacy / pre-migration workspaces). */
export const DEFAULT_WORKSPACE_TONE: WorkspaceTone = 'consultative';

export interface User {
  id: string;
  name: string;
  email: string;
}

export interface WorkspaceFile {
  id: string;
  workspace_id: string;
  filename: string;
  original_filename: string;
  file_path: string;
  file_size: number;
  formatted_file_size: string;
  mime_type: string;
  processing_status: 'pending' | 'processing' | 'completed' | 'failed';
  rag_document_id?: string;
  metadata?: any;
  extension: string;
  processed_at?: string;
  created_at: string;
}

export interface Workspace {
  id: string;
  user: User;
  name: string;
  description: string;
  instructions: string | null;
  is_default: boolean;
  rag_collection_id: string;
  total_files: number;
  processed_files: number;
  pending_files: number;
  total_file_size: number;
  formatted_file_size: string;
  searches_count: number;
  /**
   * Tone applied to enrichment output. Backend default is `consultative`;
   * `WorkspaceService.transformWorkspace` fills this in for legacy payloads.
   */
  tone: WorkspaceTone;
  /**
   * Number of leads enriched into this workspace. Optional — only present when the
   * backend exposes it; consumers MUST handle absence (hide the stat, no zero state).
   */
  enriched_count?: number;
  created_at: string;
  updated_at: string;
  files: WorkspaceFile[] | null;
  icp?: {
    id: number;
    name: string;
    description?: string;
    is_default: boolean;
  };
  // UI-specific properties
  initials?: string;
  color?: string;
}

export interface WorkspaceState {
  workspaces: Workspace[];
  selectedWorkspace: Workspace | null;
  loading: boolean;
  error: string | null;
}

export interface WorkspaceResponse {
  data: Workspace[];
  metadata: {
    message: string;
  };
}

export interface SingleWorkspaceResponse {
  data: Workspace;
  metadata: {
    message: string;
  };
}

export interface CreateWorkspacePayload {
  name: string;
  description?: string;
  instructions?: string;
  is_default?: boolean;
  create_rag_collection?: boolean;
}
